/**
 * Facets Enhancements - Real-time Product Count Updates
 * Provides real-time feedback for filter selections and product counts
 */
class FacetsEnhancements {
  constructor() {
    this.productCountElements = {
      mobile: document.querySelector("#ProductCount"),
      desktop: document.querySelector("#ProductCountDesktop"),
    };

    this.filterCheckboxes = document.querySelectorAll(
      '.facets-drawer__filter-option input[type="checkbox"]',
    );
    this.sortRadios = document.querySelectorAll(
      '.facets-drawer__option input[type="radio"]',
    );
    this.priceInputs = document.querySelectorAll(
      ".facets-drawer__price-field input",
    );

    this.currentCounts = {
      mobile: this.getProductCount(this.productCountElements.mobile),
      desktop: this.getProductCount(this.productCountElements.desktop),
    };

    this.init();
  }

  init() {
    if (
      !this.productCountElements.mobile &&
      !this.productCountElements.desktop
    ) {
      console.warn("Product count elements not found");
      return;
    }

    this.setupEventListeners();
    this.initializeCollectionCounts();
  }

  setupEventListeners() {
    // Listen for filter changes
    this.filterCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (e) => {
        this.handleFilterChange(e.target);
      });

      // Add hover effects for non-disabled filters
      const filterOption = checkbox.closest(".facets-drawer__filter-option");
      if (!filterOption.classList.contains("disabled")) {
        filterOption.addEventListener("mouseenter", () => {
          this.addHoverEffect(filterOption);
        });

        filterOption.addEventListener("mouseleave", () => {
          this.removeHoverEffect(filterOption);
        });
      }
    });

    // Listen for sort changes
    this.sortRadios.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        this.handleSortChange(e.target);
      });
    });

    // Listen for price input changes
    this.priceInputs.forEach((input) => {
      input.addEventListener("input", (e) => {
        this.handlePriceChange(e.target);
      });
    });

    // Listen for drawer apply button
    const applyButton = document.querySelector("[data-apply-filters]");
    if (applyButton) {
      applyButton.addEventListener("click", () => {
        this.showLoadingState();
      });
    }

    // Listen for clear all filters
    const clearButton = document.querySelector("[data-clear-filters]");
    if (clearButton) {
      clearButton.addEventListener("click", () => {
        this.showLoadingState();
      });
    }
  }

  handleFilterChange(checkbox) {
    const filterGroup = checkbox.closest(".facets-drawer__filter-group");
    const filterName = filterGroup.querySelector(
      ".facets-drawer__filter-name",
    ).textContent;
    const isChecked = checkbox.checked;
    const filterLabel = checkbox.nextElementSibling.textContent;
    const filterOption = checkbox.closest(".facets-drawer__filter-option");

    // Update visual feedback
    this.updateFilterVisualFeedback(checkbox, isChecked);

    // Update disabled state for related filters
    this.updateRelatedFiltersState(checkbox);

    // Calculate estimated product count
    this.updateEstimatedProductCount();

    // Announce to screen readers
    this.announceFilterChange(filterName, filterLabel, isChecked);

    // Add selection animation
    this.addSelectionAnimation(filterOption, isChecked);
  }

  handleSortChange(radio) {
    const sortLabel = radio.nextElementSibling.textContent;
    this.announceSortChange(sortLabel);
  }

  handlePriceChange(input) {
    // Debounce price input changes
    clearTimeout(this.priceTimeout);
    this.priceTimeout = setTimeout(() => {
      this.updateEstimatedProductCount();
    }, 300);
  }

  updateFilterVisualFeedback(checkbox, isChecked) {
    const filterOption = checkbox.closest(".facets-drawer__filter-option");
    const filterLabel = filterOption.querySelector("span");

    if (isChecked) {
      filterOption.style.backgroundColor = "rgba(var(--color-button), 0.1)";
      filterLabel.style.fontWeight = "600";
      checkbox.style.transform = "scale(1.1)";
    } else {
      filterOption.style.backgroundColor = "transparent";
      filterLabel.style.fontWeight = "400";
      checkbox.style.transform = "scale(1)";
    }
  }

  updateRelatedFiltersState(changedCheckbox) {
    // This is a simplified implementation
    // In a real scenario, you'd want to check filter dependencies
    const filterGroup = changedCheckbox.closest(".facets-drawer__filter-group");
    const allCheckboxes = filterGroup.querySelectorAll(
      'input[type="checkbox"]',
    );

    allCheckboxes.forEach((checkbox) => {
      const filterOption = checkbox.closest(".facets-drawer__filter-option");
      const count = parseInt(
        checkbox.nextElementSibling.getAttribute("data-count") || "0",
      );

      // Disable filters with 0 results
      if (count === 0 && !checkbox.checked) {
        filterOption.classList.add("disabled");
        checkbox.disabled = true;
      } else {
        filterOption.classList.remove("disabled");
        checkbox.disabled = false;
      }
    });
  }

  addHoverEffect(filterOption) {
    if (!filterOption.classList.contains("disabled")) {
      filterOption.style.backgroundColor =
        "rgba(var(--color-foreground), 0.05)";
      filterOption.style.transform = "translateX(2px)";
    }
  }

  removeHoverEffect(filterOption) {
    if (!filterOption.classList.contains("disabled")) {
      filterOption.style.backgroundColor = "transparent";
      filterOption.style.transform = "translateX(0)";
    }
  }

  addSelectionAnimation(filterOption, isChecked) {
    filterOption.style.transition = "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)";

    if (isChecked) {
      filterOption.style.transform = "scale(1.02)";
      setTimeout(() => {
        filterOption.style.transform = "scale(1)";
      }, 200);
    } else {
      filterOption.style.transform = "scale(0.98)";
      setTimeout(() => {
        filterOption.style.transform = "scale(1)";
      }, 200);
    }
  }

  updateEstimatedProductCount() {
    // This is a simplified estimation - in a real implementation,
    // you'd want to make an AJAX call to get the actual count
    const activeFilters = this.getActiveFiltersCount();
    const totalProducts = this.getTotalProductsCount();

    // Simple estimation logic - adjust based on your data
    let estimatedCount = totalProducts;
    if (activeFilters > 0) {
      // Rough estimation: each filter reduces count by ~30%
      estimatedCount = Math.max(
        1,
        Math.floor(totalProducts * Math.pow(0.7, activeFilters)),
      );
    }

    this.updateProductCountDisplay(estimatedCount, true); // true = estimated
  }

  updateProductCountDisplay(count, isEstimated = false) {
    const countText = this.formatCountText(count, isEstimated);

    if (this.productCountElements.mobile) {
      this.animateCountChange(this.productCountElements.mobile, countText);
    }

    if (this.productCountElements.desktop) {
      this.animateCountChange(this.productCountElements.desktop, countText);
    }
  }

  animateCountChange(element, newText) {
    // Add animation class
    element.style.transition = "opacity 0.3s ease";
    element.style.opacity = "0.5";

    setTimeout(() => {
      element.textContent = newText;
      element.style.opacity = "1";
    }, 150);
  }

  formatCountText(count, isEstimated) {
    const countNumber = parseInt(count);

    if (isEstimated) {
      return `~${countNumber} products`;
    }

    if (countNumber === 1) {
      return "1 product";
    }

    return `${countNumber} products`;
  }

  getActiveFiltersCount() {
    let count = 0;

    // Count checked checkboxes
    this.filterCheckboxes.forEach((checkbox) => {
      if (checkbox.checked) count++;
    });

    // Count active price filters
    this.priceInputs.forEach((input) => {
      if (input.value && input.value !== input.placeholder) count++;
    });

    return count;
  }

  getTotalProductsCount() {
    // Get the initial total from the current display
    const mobileText = this.getProductCount(this.productCountElements.mobile);
    const desktopText = this.getProductCount(this.productCountElements.desktop);

    // Extract number from text like "24 products" or "24 of 48 products"
    const mobileMatch = mobileText.match(/(\d+)/);
    const desktopMatch = desktopText.match(/(\d+)/);

    if (mobileMatch) return parseInt(mobileMatch[1]);
    if (desktopMatch) return parseInt(desktopMatch[1]);

    return 0;
  }

  getProductCount(element) {
    if (!element) return "";
    return element.textContent.trim();
  }

  showLoadingState() {
    // Add loading class to product count elements
    if (this.productCountElements.mobile) {
      this.productCountElements.mobile
        .closest(".product-count")
        .classList.add("loading");
    }

    if (this.productCountElements.desktop) {
      this.productCountElements.desktop
        .closest(".product-count")
        .classList.add("loading");
    }
  }

  announceFilterChange(filterName, filterLabel, isChecked) {
    const action = isChecked ? "added" : "removed";
    const announcement = `${filterLabel} filter ${action} from ${filterName}`;
    this.announceToScreenReader(announcement);
  }

  announceSortChange(sortLabel) {
    const announcement = `Sort changed to ${sortLabel}`;
    this.announceToScreenReader(announcement);
  }

  announceToScreenReader(message) {
    const announcement = document.createElement("div");
    announcement.setAttribute("aria-live", "polite");
    announcement.setAttribute("aria-atomic", "true");
    announcement.className = "visually-hidden";
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  initializeCollectionCounts() {
    // Add count badges to collection navigation if they don't exist
    const collectionButtons = document.querySelectorAll(
      ".collection-nav-button",
    );

    collectionButtons.forEach((button) => {
      const countElement = button.querySelector(
        ".collection-nav-button__count",
      );
      if (countElement) {
        // Add subtle animation on load
        countElement.style.animation = "fadeIn 0.5s ease";
      }
    });
  }
}

// Auto-initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  window.facetsEnhancements = new FacetsEnhancements();
});

// Also handle dynamic content loading
document.addEventListener("shopify:section:load", () => {
  if (window.facetsEnhancements) {
    window.facetsEnhancements = new FacetsEnhancements();
  }
});

// Add CSS animation
const style = document.createElement("style");
style.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.8); }
    to { opacity: 1; transform: scale(1); }
  }
`;
document.head.appendChild(style);
